import { Form } from "@circle-react-uikit/Form";
import { Modal } from "@circleco/compass/components/Modal";

import { t } from "@/i18n-js/instance";
import type { ReportFormValues } from "@/react/components/CommentsCompass/data/useReportComment";
import { useReportComment } from "@/react/components/CommentsCompass/data/useReportComment";
import {
  ModalManager,
  useModal,
} from "@/react/components/Layout/ModalManagerProvider";
import { WorkflowsMomentumModal } from "@/react/components/WorkflowsMomentumModal";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";
import type { Comment } from "@/react/types/Comment";

const FORM_ID = "report-comment-modal-form";

const REPORT_REASONS = [
  "harassment",
  "spam",
  "incorrect_location",
  "against_guidelines",
  "other",
] as const;

const REPORT_REASON_LABELS = {
  harassment: () => t("comments.report.form.reasons.harassment"),
  spam: () => t("comments.report.form.reasons.spam"),
  incorrect_location: () =>
    t("comments.report.form.reasons.incorrect_location"),
  against_guidelines: () =>
    t("comments.report.form.reasons.against_guidelines"),
  other: () => t("comments.report.form.reasons.other"),
} as const;

interface ReportModalProps {
  comment: Comment;
}

const ReportModal = ModalManager.create(({ comment }: ReportModalProps) => {
  const modal = useModal();
  const { currentCommunityMember } = usePunditUserContext();

  const { reportComment, isPending } = useReportComment({
    comment,
    onSuccess: () => {
      if (isCommunityAdmin(currentCommunityMember)) {
        void ModalManager.show(WorkflowsMomentumModal, {
          templateKey: "ai_moderate_new_comments",
        });
      }
      void modal.hide();
    },
  });

  const reportReasonOptions = REPORT_REASONS.map(reason => ({
    value: reason,
    label:
      REPORT_REASON_LABELS[reason]?.() ??
      t("comments.report.form.reasons.other"),
  }));

  return (
    <Modal.Root
      open={modal.visible}
      onOpenChange={open => {
        if (!open) void modal.hide();
      }}
      onOpenChangeComplete={open => {
        if (!open) modal.remove();
      }}
      size="md"
    >
      <Modal.Content>
        <Modal.Header title={t("comments.report.form.title")} />
        <Modal.Body>
          <Form<ReportFormValues>
            id={FORM_ID}
            defaultValues={{
              reported_reason_body: "",
              reported_reason_type: "",
            }}
            onSubmit={formData => reportComment(formData)}
          >
            <Form.Item
              hideBorder
              isFirstChild
              name="reported_reason_type"
              rules={{
                required: t("comments.report.form.this_field_is_required"),
              }}
              translationRoot="comments.report.form"
            >
              <Form.SelectNative
                name="reported_reason_type"
                options={reportReasonOptions}
                prompt={t("comments.report.form.reported_reason_type_select")}
              />
            </Form.Item>
            <Form.Item
              fullWidth
              hideBorder
              name="reported_reason_body"
              translationRoot="comments.report.form"
            >
              <Form.TextArea name="reported_reason_body" />
            </Form.Item>
          </Form>
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t("comments.report.actions.cancel"),
            onClick: () => void modal.hide(),
          }}
          primaryAction={{
            type: "submit",
            form: FORM_ID,
            label: t("comments.report.actions.submit"),
            loading: isPending,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
});

export const useReportModal = () => useModal(ReportModal);
